import { diff_match_patch as DiffMatchPatch } from 'diff-match-patch';
import parserHtml from 'prettier/parser-html';
import parserBabel from 'prettier/parser-babel';
import prettier from 'prettier/standalone';

// This class contains helpers to format html and it's embedded js code using prettier.
export default class HtmlFormatter {
  constructor() {
    this.plugins = [parserHtml, parserBabel];
    this.parser = 'html';
  }

  getPlugins() {
    return this.plugins;
  }

  setPlugins(plugins) {
    this.plugins = plugins;
  }

  getParser() {
    return this.parser;
  }

  setParser(parser) {
    this.parser = parser;
  }

  /**
   * Checks to see if the text has been formatted already.
   *
   * @param {string} text
   * @returns {boolean}
   */
  formatCheck(text) {
    if (text) {
      try {
        return prettier.check(text, {
          plugins: this.plugins,
          parser: this.parser,
        });
      } catch (error) {
        throw new Error(`Failed to check if formatted with error: ${error.message}`);
      }
    }
    return false;
  }

  /**
   * This function takes in a string with a selection and returns it formatted with new selection after formatting.
   * The head is where the selection starts and the anchor is where it ends.
   * Input: (unformattedText: string, selection: {anchor: number, head: number})
   * Output: {formattedText: string, selection: {anchor: number, head: number}}
   *
   * @param {string} unformattedText
   * @param {Object} selection
   * @returns {Object}
   */
  formatWithSelection(unformattedText, selection) {
    if (unformattedText && selection && selection.head && selection.anchor) {
      try {
        const formattedText = prettier.format(unformattedText, {
          parser: this.parser,
          plugins: this.plugins,
        });
        const dmp = new DiffMatchPatch();
        /**
         * Returns an array of differences between the old unformatted text and new formatted text.
         * Each difference = [status - insert(1), delete(-1), stay(0): num, item: string]
         */
        const diff = dmp.diff_main(unformattedText, formattedText);
        /**
         * Find the position of the cursor in unformattedText translated formattedText.
         * We want the cursor at selection.head and selection.anchor.
         */
        const newSelectionHead = dmp.diff_xIndex(diff, selection.head);
        const newSelectionAnchor = dmp.diff_xIndex(diff, selection.anchor);
        const newSelection = { head: newSelectionHead, anchor: newSelectionAnchor };
        return { formattedText, selection: newSelection };
      } catch (error) {
        throw new Error(`Failed to formatWithSelection with error: ${error.message}`);
      }
    }
    return { formattedText: unformattedText, selection };
  }

  /**
   * This function takes in a unformattedText and returns formattedText if possible.
   * If it fails it will return the unformattedText.
   *
   * @param {string} unformattedText
   * @returns {string}
   */
  format(unformattedText) {
    if (unformattedText) {
      try {
        return prettier.format(unformattedText, {
          parser: this.parser,
          plugins: this.plugins,
        });
      } catch (error) {
        throw new Error(`Failed to format with error: ${error.message}`);
      }
    }
    return unformattedText;
  }
}
